import { Fragment, useEffect, useMemo } from "react";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import { dividerState, ortexColorState, textColorState, userState } from "@/lib/store";
import Link from "next/link";
import MenuPopup from "../menu-popup/MenuPopup";
import { useRecoilValue } from "recoil";
import { useState } from "react";
import { createPortal } from "react-dom";
import styles from "./Menu.module.scss";
import { Chip, Divider, Typography, useMediaQuery } from "@mui/material";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { useRouter } from "next/router";
import { classNames } from "@/lib/utils";
import { useTradingIdeasAccess } from "src/hooks/useTradingIdeasAccess";

const COMING_SOON = "/coming-soon";

const menuList = [
    {
        icon: "fal fa-code",
        text: "ORTEX APIs",
        link: "/apis/plans",
        tag: "New",
    },
    {
        icon: "fal fa-brain",
        text: "Market Intelligence",
        link: "/market-intelligence",
        tag: "New",
    },
    {
        // requiresTradingIdeasAccess: only shown once the backend
        // access probe confirms this user can see the feature — see
        // useTradingIdeasAccess and the visibleMenuList filter below.
        icon: "fal fa-lightbulb",
        text: "Trading Ideas",
        link: "/trading-ideas",
        tag: "New",
        requiresTradingIdeasAccess: true,
    },
    {
        icon: "fak fa-markets-overview",
        text: "Markets Overview",
        link: "/markets-overview",
    },
    {
        link: "/market-sentiment",
        icon: "fak fa-data-sentiment",
        text: "Market Sentiment",
    },
    {
        icon: "fak fa-stock-data-sets",
        text: "Stock Data Sets",
        submenu: [
            {
                icon: "fal fa-chart-line-down",
                href: "/short-interest",
                text: "Short Interest",
            },
            {
                icon: "fak fa-stock-options",
                href: "/options",
                text: "Options",
            },
            {
                icon: "fal fa-users",
                href: "/insiders",
                text: "Insiders",
            },
            {
                icon: "fal fa-user-tie",
                href: "/analysts",
                text: "Analysts",
            },
            {
                icon: "fal fa-newspaper",
                href: "/news",
                text: "News",
            },
            {
                icon: "fal fa-calendar-alt",
                href: "/events",
                text: "Company Events",
            },

            {
                icon: "fal fa-list-ol",
                href: "/holdings",
                text: "Holdings",
            },
            {
                icon: "fal fa-arrows-to-circle",
                href: "/buybacks",
                text: "Buybacks",
                tag: "New",
            },
            {
                icon: "fal fa-chart-pie-alt",
                href: "/dividends",
                text: "Dividends",
            },
            {
                icon: "fal fa-split",
                text: "Stock Splits",
                href: "/stock-splits",
            },
            {
                icon: "fal fa-balance-scale",
                href: "/index-rebalance",
                text: "Index Rebalance",
            },
            // {
            //     icon: "fak fa-credit-default-swaps",
            //     href: "/cds",
            //     text: "Credit Default Swaps",
            // },
            {
                href: "/us-gov-trades",
                icon: "fak fa-building-columns-regular",
                text: "US Government Trades",
            },
            {
                href: "/alt-data",
                icon: "fal fa-database",
                text: "Alt Data",
                tag: "New",
                requiresAltData: true,
            },
            {
                href: "/bonds",
                icon: "fal fa-file-certificate",
                text: "Bonds",
                tag: "New",
                requiresAltData: true,
            },
            {
                // Unemployment, inflation and claims: the macro datasets that used to sit inside Alt Data.
                href: "/macro-data",
                icon: "fal fa-globe",
                text: "Macro Data",
                tag: "New",
                requiresAltData: true,
            },
            {
                href: "/activist-filings",
                icon: "fal fa-bullhorn",
                text: "Activist Filings",
                tag: "New",
            },
        ],
    },

    {
        link: "/money-flow",
        icon: "fak fa-passive-money-flow",
        text: "Money Flow",
    },

    {
        link: "/index-rebalance",
        icon: "fal fa-balance-scale",
        text: "Index Rebalance",
    },

    {
        link: "/stock-movers",
        icon: "fak fa-charts",
        text: "Stock Movers",
    },

    {
        link: "/stock-stamps",
        icon: "fak fa-stock-stamps",
        text: "Stock Stamps",
    },

    {
        link: "/portfolio",
        icon: "fak fa-portfolio",
        text: "My Portfolio",
    },

    {
        link: "/my-universe",
        icon: "fak fa-my-universe",
        text: "My Stocks",
    },
    {
        icon: "fal fa-newspaper",
        link: "/news",
        text: "News",
    },
    {
        icon: "fal fa-calendar-alt",
        text: "Events",
        submenu: [
            {
                icon: "fal fa-calendar-alt",
                text: "Company Events",
                href: "/events",
            },
            {
                icon: "fal fa-globe-europe",
                text: "Macro Events",
                href: "/macro",
            },
            {
                icon: "fak fa-stock-trading-signals",
                text: "Earnings HeatMap",
                href: "/earnings/heatmap",
            },
            {
                icon: "fal fa-split",
                text: "Stock Splits",
                href: "/stock-splits",
            },
        ],
    },
    {
        icon: "fal fa-list-ol",
        text: "Ownership",
        submenu: [
            {
                href: "/holdings",
                icon: "fal fa-list-ol",
                text: "Holdings",
            },
            {
                href: "/buybacks",
                icon: "fal fa-arrows-to-circle",
                text: "Buybacks",
                tag: "New",
            },
            {
                href: "/insiders",
                icon: "fal fa-users",
                text: "Insiders",
            },
            {
                href: "/ipo-deals",
                icon: "fak fa-ipos-deals",
                text: "IPOs & Deals",
            },
            {
                href: "/lock-ups",
                icon: "fak fa-lock-solid-2",
                text: "Lock Ups",
            },
            {
                href: "/us-gov-trades",
                icon: "fak fa-building-columns-regular",
                text: "US Government Trades",
            },
            {
                href: "/alt-data",
                icon: "fal fa-database",
                text: "Alt Data",
                tag: "New",
                requiresAltData: true,
            },
            {
                // Unemployment, inflation and claims: the macro datasets that used to sit inside Alt Data.
                href: "/macro-data",
                icon: "fal fa-globe",
                text: "Macro Data",
                tag: "New",
                requiresAltData: true,
            },
            {
                href: "/activist-filings",
                icon: "fal fa-bullhorn",
                text: "Activist Filings",
                tag: "New",
            },
        ],
    },
    {
        link: "/trading-signals",
        icon: "fak fa-trading-signals",
        text: "Trading Signals",
    },

    {
        link: "/stock-scores",
        icon: "fak fa-stock-scores",
        text: "Stock Scores",
    },

    {
        link: "/sector-scores",
        icon: "fak fa-ranking-star",
        text: "Sector Scores",
    },
    // {
    //     link: "/cds",
    //     icon: "fak fa-credit-default-swaps",
    //     text: "Credit Default Swaps",
    // },
    {
        link: "/etfs",
        icon: "fak fa-etfs",
        text: "ETFs",
        submenu: [
            {
                icon: "fak fa-etfs",
                href: "/etfs",
                text: "ETF Overview",
            },
            {
                icon: "fak fa-etfs",
                href: "/money-flow",
                text: "ETF Fund Flow",
            },
            {
                icon: "fak fa-etfs",
                href: "/etf-shorts",
                text: "ETF Shorts",
            },
        ],
    },

    {
        icon: "fak fa-screening",
        text: "Charts & Screening",
        submenu: [
            {
                icon: "fak fa-stock-trading-signals",
                href: "/charts",
                text: "Charts",
            },
            {
                icon: "fak fa-screening",
                href: "/screening",
                text: "Screening",
            },

            {
                icon: "fak fa-stock-trading-signals",
                href: "/heatmap",
                text: "HeatMaps",
            },
            {
                icon: "fal fa-chart-line-down",
                text: "Short Screener",
                href: "/short-interest?tab=short_extremes",
            },
            {
                icon: "fal fa-chart-pie-alt",
                href: "/dividends?tab=screener",
                text: "Dividends Screener",
            },
        ],
    },
    {
        link: "",
        icon: "",
        text: "Popular",
    },
    {
        icon: "fal fa-code",
        text: "ORTEX APIs",
        link: "/apis/plans",
        tag: "New",
    },
    {
        icon: "fal fa-brain",
        text: "Market Intelligence",
        link: "/market-intelligence",
        tag: "New",
    },
    {
        // requiresTradingIdeasAccess: only shown once the backend
        // access probe confirms this user can see the feature — see
        // useTradingIdeasAccess and the visibleMenuList filter below.
        icon: "fal fa-lightbulb",
        text: "Trading Ideas",
        link: "/trading-ideas",
        tag: "New",
        requiresTradingIdeasAccess: true,
    },
    {
        icon: "fak fa-screening",
        link: "/screening",
        text: "Screening",
    },
    {
        icon: "fal fa-chart-line-down",
        text: "Short Interest",
        submenu: [
            {
                icon: "fal fa-chart-line-down",
                text: "Overview",
                href: "/short-interest?tab=overview",
            },
            {
                icon: "fal fa-chart-line-down",
                text: "Live Short Screener",
                href: "/short-interest?tab=live_short_screener",
            },
            {
                icon: "fal fa-chart-line-down",
                text: "Short Extremes",
                href: "/short-interest?tab=short_extremes",
            },
            {
                icon: "fal fa-chart-line-down",
                text: "Short Changes",
                href: "/short-interest?tab=short_changes",
            },
            {
                icon: "fal fa-chart-line-down",
                text: "Short HeatMap",
                href: "/short-interest?tab=heatmap",
            },
            {
                icon: "fal fa-chart-line-down",
                text: "Sector Averages",
                href: "/short-interest?tab=sector_averages",
            },
            {
                icon: "fal fa-chart-line-down",
                text: "Index Averages",
                href: "/short-interest?tab=index_averages",
            },
            {
                icon: "fal fa-chart-line-down",
                text: "European Short Flags",
                href: "/short-interest?tab=european_short_reports",
            },
            {
                icon: "fal fa-chart-line-down",
                text: "European Short Holders",
                href: "/short-interest?tab=european_short_holders",
            },
        ],
    },

    {
        icon: "fak fa-stock-options",
        text: "Options",
        submenu: [
            // Options Platform addon — premium analytics. New routes under
            // /options-platform/* are gated by AddonGate. Chain + Research
            // are per-ticker only (no cross-ticker landing) so they're not
            // top-nav entries — users reach them via the per-stock Options
            // tab on any US stock page.
            //
            // requiresOptionsPlatform: only shown to users entitled to the
            // new interface (the PRODUCT_OPTIONS_PLATFORM addon or the
            // new-options-UI rollout group). Non-members get the legacy
            // entries below only — filtered in MenuListItems.
            {
                icon: "fak fa-stock-options",
                text: "Flow",
                href: "/options-platform/flow",
                requiresOptionsPlatform: true,
            },
            {
                icon: "fak fa-stock-options",
                text: "Analyze",
                href: "/options-tools/analyze",
                requiresOptionsPlatform: true,
            },
            // Legacy free chain views — kept for users without the addon.
            {
                icon: "fak fa-stock-options",
                text: "Interesting Options Flow",
                href: "/options?tab=interesting-options-flow",
            },
            {
                icon: "fak fa-stock-options",
                text: "Options Flow Sentiment",
                href: "/options?tab=option-flow-sentiment",
            },
            {
                icon: "fak fa-stock-options",
                text: "Options Extremes",
                href: "/coming-soon",
            },
        ],
    },
    {
        icon: "fal fa-users",
        text: "Insiders",
        link: "/insiders",
    },

    {
        icon: "fal fa-user-tie",
        text: "Analysts",
        link: "/analysts",
    },
    {
        icon: "fak fa-stock-trading-signals",
        text: "HeatMaps",
        link: "/heatmap",
    },
];

type MenuEntry = { icon?: string; link?: string; href?: string; submenu?: MenuEntry[] };

/**
 * The menu's icon class for a page path ("/analysts" -> "fal fa-user-tie"), so other surfaces -
 * the search dropdown - show the same icon as the menu. Query strings are ignored.
 */
export function menuIconFor(path: string | undefined): string | undefined {
    if (!path) return undefined;
    const wanted = path.split("?")[0];
    const walk = (entries: MenuEntry[]): string | undefined => {
        for (const entry of entries) {
            const target = (entry.link ?? entry.href ?? "").split("?")[0];
            if (target && target === wanted && entry.icon) return entry.icon;
            const found = entry.submenu ? walk(entry.submenu) : undefined;
            if (found) return found;
        }
        return undefined;
    };
    return walk(menuList as MenuEntry[]);
}

export const MenuListItems = ({ isIconOnly = false }: { isIconOnly?: boolean }) => {
    const [selectedItem, setSelectedItem] = useState(undefined);
    const [minMenuHovered, setMinMenuHovered] = useState(false);
    const isnotMobile = useMediaQuery("(min-width: 768px)");

    const router = useRouter();

    const textColor = useRecoilValue(textColorState);
    const dividerColor = useRecoilValue(dividerState);
    const ortexColor = useRecoilValue(ortexColorState);
    const user = useRecoilValue(userState);

    // Hide the Options Platform submenu entries from users without the
    // addon / new-UI rollout — same entitlement pair StockLinks and
    // useOptionsUiRedirect use. Filtering only submenu items keeps every
    // top-level index aligned, so the rest of the render (which indexes
    // visibleMenuList[selectedItem]) is unaffected.
    const canSeeOptionsPlatform = !!(user?.options_platform_subscriber || user?.new_options_ui);
    // Alt Data is behind its own entitlement (PRODUCT_ALT_DATA) while the
    // datasets are backfilled and ticker-mapped. Straight off userState like
    // the Options Platform pair — the backend puts `alt_data` on the
    // /API/v2/user payload — so no probe is needed. Undefined (the early-return
    // branches of the settings payload) is falsy, so this fails closed.
    const canSeeAltData = !!user?.alt_data;
    // Trading Ideas is gated by a backend access probe (staff + a
    // server-side group — not expressible from userState, unlike the
    // Options Platform pair above). Hidden until the probe confirms
    // access; the top-level filter is safe for the
    // visibleMenuList[selectedItem] lookups because every index in the
    // render comes from mapping this same filtered array, and the list
    // only changes once, when the probe resolves — selectedItem is
    // hover-transient and re-derives on the next mouseover.
    const { access: tradingIdeasAccess } = useTradingIdeasAccess();
    const visibleMenuList = useMemo(
        () =>
            menuList
                .filter((item) => tradingIdeasAccess === true || !(item as { requiresTradingIdeasAccess?: boolean }).requiresTradingIdeasAccess)
                .map((item) =>
                    "submenu" in item && item.submenu
                        ? {
                              ...item,
                              submenu: item.submenu.filter(
                                  (s) =>
                                      (canSeeOptionsPlatform || !(s as { requiresOptionsPlatform?: boolean }).requiresOptionsPlatform) &&
                                      (canSeeAltData || !(s as { requiresAltData?: boolean }).requiresAltData),
                              ),
                          }
                        : item,
                ),
        [canSeeOptionsPlatform, canSeeAltData, tradingIdeasAccess],
    );

    const handleHover = (key: number, isMinMenu?: boolean) => {
        setSelectedItem(key);
        setMinMenuHovered(isMinMenu);
    };

    return (
        <>
            <div className={styles.MenuOverlay} onMouseEnter={() => setSelectedItem(undefined)}></div>
            <List sx={{ textAlign: "center", paddingY: 0.5 }} className={styles.list} id="list">
                {visibleMenuList.map((item, index, to) =>
                    !isIconOnly ? (
                        item.text === "Popular" ? (
                            <Fragment key={index}>
                                <Divider
                                    sx={{
                                        "&::before, &::after": {
                                            borderColor: dividerColor,
                                        },
                                    }}
                                >
                                    <Chip variant="outlined" label="Popular" sx={{ backgroundColor: dividerColor, color: textColor, mt: 1, mb: 1 }} />
                                </Divider>
                            </Fragment>
                        ) : (
                            <ListItem disablePadding key={index} onMouseOver={() => handleHover(index)} id={`menu-item-${index}`} className={styles.mainLink}>
                                {!item.submenu ? (
                                    <Link href={item.link} passHref>
                                        <ListItemButton className={styles["dynamic-padding"]} sx={{ display: "flex", justifyContent: "center", alignItems: "center" }}>
                                            <ListItemIcon>
                                                <div className={classNames(item.icon, styles["menu-fontsize"])} style={{ color: textColor, marginLeft: "4px" }} />
                                            </ListItemIcon>
                                            <ListItemText sx={{ margin: 0 }}>
                                                <Typography variant="h5userMenu"> {item.text} </Typography>
                                            </ListItemText>
                                            {item.tag && (
                                                <Chip
                                                    label={item.tag}
                                                    size="small"
                                                    variant="outlined"
                                                    className={styles.menuTag}
                                                    sx={{
                                                        height: "18px",
                                                        fontSize: "10px",
                                                        marginLeft: 1,
                                                        color: ortexColor,
                                                        borderColor: ortexColor,
                                                        "& .MuiChip-label": {
                                                            padding: "0 6px",
                                                        },
                                                    }}
                                                />
                                            )}
                                        </ListItemButton>
                                    </Link>
                                ) : !isnotMobile ? (
                                    <ListItemButton className={styles["dynamic-padding"]} onClick={(e: React.KeyboardEvent | React.MouseEvent) => e.stopPropagation()} sx={{ display: "flex", justifyContent: "center", alignItems: "center" }}>
                                        <ListItemIcon>
                                            <div className={classNames(item.icon, styles["menu-fontsize"])} style={{ color: textColor, marginLeft: "4px" }} />
                                        </ListItemIcon>
                                        <ListItemText sx={{ margin: 0 }}>
                                            <Typography variant="h5userMenu">{item.text}</Typography>
                                        </ListItemText>
                                        {item.tag && (
                                            <Chip
                                                label={item.tag}
                                                size="small"
                                                variant="outlined"
                                                className={styles.menuTag}
                                                sx={{
                                                    height: "18px",
                                                    fontSize: "10px",
                                                    marginLeft: 1,
                                                    marginRight: 0.5,
                                                    color: ortexColor,
                                                    borderColor: ortexColor,
                                                    "& .MuiChip-label": {
                                                        padding: "0 6px",
                                                    },
                                                }}
                                            />
                                        )}
                                        <ChevronRightIcon className={styles["menu-fontsize"]} sx={{ color: textColor }} />
                                    </ListItemButton>
                                ) : selectedItem && visibleMenuList[selectedItem].submenu ? (
                                    <Link href={visibleMenuList[selectedItem].submenu[0].href} passHref>
                                        {/* the following code is the same as the one below, but enclosed in a link */}
                                        <ListItemButton className={styles["dynamic-padding"]} onClick={(e: React.KeyboardEvent | React.MouseEvent) => e.stopPropagation()} sx={{ display: "flex", justifyContent: "center", alignItems: "center" }}>
                                            <ListItemIcon>
                                                <div className={classNames(item.icon, styles["menu-fontsize"])} style={{ color: textColor, marginLeft: "4px" }} />
                                            </ListItemIcon>
                                            <ListItemText sx={{ margin: 0 }}>
                                                <Typography variant="h5userMenu">{item.text}</Typography>
                                            </ListItemText>
                                            {item.tag && (
                                                <Chip
                                                    label={item.tag}
                                                    size="small"
                                                    variant="outlined"
                                                    className={styles.menuTag}
                                                    sx={{
                                                        height: "18px",
                                                        fontSize: "10px",
                                                        marginLeft: 1,
                                                        marginRight: 0.5,
                                                        color: ortexColor,
                                                        borderColor: ortexColor,
                                                        "& .MuiChip-label": {
                                                            padding: "0 6px",
                                                        },
                                                    }}
                                                />
                                            )}
                                            <ChevronRightIcon className={styles["menu-fontsize"]} sx={{ color: textColor }} />
                                        </ListItemButton>
                                    </Link>
                                ) : (
                                    <>
                                        {/* Popular menu items */}
                                        <ListItemButton className={styles["dynamic-padding"]} onClick={(e: React.KeyboardEvent | React.MouseEvent) => e.stopPropagation()} sx={{ display: "flex", justifyContent: "center", alignItems: "center" }}>
                                            <ListItemIcon>
                                                <div className={classNames(item.icon, styles["menu-fontsize"])} style={{ color: textColor, marginLeft: "4px" }} />
                                            </ListItemIcon>
                                            <ListItemText sx={{ margin: 0 }}>
                                                <Typography variant="h5userMenu">{item.text}</Typography>
                                            </ListItemText>
                                            {item.tag && (
                                                <Chip
                                                    label={item.tag}
                                                    size="small"
                                                    variant="outlined"
                                                    className={styles.menuTag}
                                                    sx={{
                                                        height: "18px",
                                                        fontSize: "10px",
                                                        marginLeft: 1,
                                                        marginRight: 0.5,
                                                        color: ortexColor,
                                                        borderColor: ortexColor,
                                                        "& .MuiChip-label": {
                                                            padding: "0 6px",
                                                        },
                                                    }}
                                                />
                                            )}
                                            <ChevronRightIcon className={styles["menu-fontsize"]} sx={{ color: textColor }} />
                                        </ListItemButton>
                                    </>
                                )}
                            </ListItem>
                        )
                    ) : (
                        <ListItem disablePadding key={index} onMouseOver={() => handleHover(index, true)} id={`small-menu-item-${index}`}>
                            {item.text === "Popular" ? null : !item.submenu ? (
                                <Link href={item.link} passHref>
                                    <ListItemButton className={styles["dynamic-padding"]} sx={{ display: "flex", justifyContent: "center", alignItems: "center" }}>
                                        <ListItemIcon>
                                            <div className={classNames(item.icon, styles["sidemenu-fontsize"])} style={{ color: textColor }} />
                                        </ListItemIcon>
                                    </ListItemButton>
                                </Link>
                            ) : (
                                <>
                                    {selectedItem && visibleMenuList[selectedItem].submenu ? (
                                        <Link href={visibleMenuList[selectedItem].submenu[0].href} passHref>
                                            <ListItemButton className={styles["dynamic-padding"]} onClick={(e: React.KeyboardEvent | React.MouseEvent) => e.stopPropagation()} sx={{ display: "flex", justifyContent: "center", alignItems: "center" }}>
                                                <ListItemIcon>
                                                    <div className={classNames(item.icon, styles["sidemenu-fontsize"])} style={{ color: textColor }} />
                                                </ListItemIcon>
                                            </ListItemButton>
                                        </Link>
                                    ) : (
                                        <ListItemButton className={styles["dynamic-padding"]} onClick={(e: React.KeyboardEvent | React.MouseEvent) => e.stopPropagation()} sx={{ display: "flex", justifyContent: "center", alignItems: "center" }}>
                                            <ListItemIcon>
                                                <div className={classNames(item.icon, styles["sidemenu-fontsize"])} style={{ color: textColor }} />
                                            </ListItemIcon>
                                        </ListItemButton>
                                    )}
                                </>
                            )}
                        </ListItem>
                    )
                )}
            </List>
            {selectedItem !== undefined &&
                visibleMenuList[selectedItem].submenu !== undefined &&
                createPortal(
                    <MenuPopup links={visibleMenuList[selectedItem].submenu} index={selectedItem} parentId={minMenuHovered ? "small-menu-item-" : "menu-item-"} />,
                    minMenuHovered ? document.getElementById("sideMenu") : document.getElementById("menu") || document.body
                )}
        </>
    );
};
